import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';

import { CustomerSearchResult } from '../../../../../../models/customer';
import { ListResponseCountOptional } from '../../../../../../models/list-response';
import { getPaymentMethodTypeDisplay, Payment } from '../../../../../../models/payment';
import { AccountService } from '../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { PaymentService } from '../../../../../../services/payment/payment.service';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../utils';


@Component({
  selector: 'app-change-payment-customer-modal',
  templateUrl: './change-payment-customer-modal.component.html',
  styleUrls: ['./change-payment-customer-modal.component.css']
})
export class ChangePaymentCustomerModalComponent implements OnInit {

  @Input() paymentId: number;
  @Input() savedCallback: (successMessage: string) => void;

  public payment: Payment | null = null;

  public selectedCustomer: CustomerSearchResult | null = null;

  public loadingSubmit = false;

  public serverError: number = null;

  public errorMessage = '';

  public loadingCustomers = false;
  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult> = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly getPaymentMethodTypeDisplay = getPaymentMethodTypeDisplay;

  // Only the most recent search request's results are displayed.
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    private accountService: AccountService,
    private customerService: CustomerService,
    private paymentService: PaymentService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.paymentService
      .getPayment(this.paymentId)
      .then(payment => {
        this.payment = payment;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      });

    this.customerFiltersChangedSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  public handleCustomerFilterChange() {
    this.loadingCustomers = true;

    this.selectedCustomer = null;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  public loadCustomers() {
    this.loadingCustomers = true;

    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  public handleSelectedCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = customer.first_name + ' ' + customer.last_name;
    this.customerList = null;
  }

  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.filteredCustomerResults().length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    const currentCustomerId = this.payment ? this.payment.customer_id : undefined;
    return this.customerList.results.filter(
      (customer) => (currentCustomerId !== customer.id)
    );
  }

  public hasLinkedCustomer(): boolean {
    return propExists(this.payment.customer_id);
  }

  public handleSave() {
    if (this.selectedCustomer === null || this.payment === null) { return; }

    const customer = this.selectedCustomer;

    this.loadingSubmit = true;
    this.errorMessage = '';

    this.paymentService
      .changePaymentCustomer(this.paymentId, customer.id)
      .then(() => {
        this.savedCallback('Payment linked to ' + customer.first_name + ' ' + customer.last_name + '.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to change the customer linked to this payment.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error.useful_error_message;
        } else if (reason.status === 404) {
          this.errorMessage = 'Customer not found.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }
}
